import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Database } from '@pirouette/db';
import { stripUndefined } from '@/lib/db';
import { supabase } from '@/lib/supabase';

// Costume Command Center data layer.
//
// Mirrors the shape of @/data/reports.ts: thin typed react-query wrappers over
// `supabase.rpc(...)` and `supabase.from(...)`. Every result type is derived
// from the generated `@pirouette/db` Database types so the UI stays in lockstep
// with the database contract. Nothing here bypasses RLS — each call runs as the
// authenticated admin and the per-table policies scope it to the caller's
// studio (tenant).

type RpcReturns<K extends keyof Database['public']['Functions']> =
  Database['public']['Functions'][K]['Returns'];

// `costume_status_report` is declared `RETURNS TABLE (...)`, so PostgREST hands
// it back as a one-row array. We surface the single row (or null) to callers.
export type CostumeStatusReport = RpcReturns<'costume_status_report'>[number];

// One assignment row from the production detail RPC. `flags` is `Json`; the
// recommender writes a string[] of advisory codes (e.g. "no_measurement",
// "low_confidence"), which we narrow at the read boundary.
export type CostumeProductionDetailRow = RpcReturns<'costume_production_detail'>[number];

export type CostumeFulfillmentStatus = Database['public']['Enums']['costume_fulfillment_status'];
export type CostumePaymentStatus = Database['public']['Enums']['costume_payment_status'];

/** Scope kinds accepted by the `assign_costumes` RPC. */
export type CostumeAssignmentScope = 'company' | 'class' | 'dance';

/**
 * Shape returned by `recommend_size_preview` (typed `Json` in the DB). The RPC
 * returns `{ size, confidence, flags[] }`; we model it explicitly so callers do
 * not have to re-narrow `Json` at every use site. Treat every field as possibly
 * absent — it is computed server-side from whatever profile data exists.
 */
export interface SizeRecommendationPreview {
  size: string | null;
  confidence: number | null;
  flags: string[];
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Readiness rollup for a production: counts + cost/paid cents + exceptions. */
export function useCostumeStatusReport(productionId: string | undefined) {
  return useQuery({
    enabled: !!productionId,
    queryKey: ['costumes', 'status-report', productionId ?? null],
    queryFn: async (): Promise<CostumeStatusReport | null> => {
      const { data, error } = await supabase.rpc('costume_status_report', {
        p_production_id: productionId!,
      });
      if (error) throw error;
      return data?.[0] ?? null;
    },
  });
}

/** Per-assignment detail rows (dancer, costume, sizing, fulfillment, payment). */
export function useCostumeProductionDetail(productionId: string | undefined) {
  return useQuery({
    enabled: !!productionId,
    queryKey: ['costumes', 'production-detail', productionId ?? null],
    queryFn: async (): Promise<CostumeProductionDetailRow[]> => {
      const { data, error } = await supabase.rpc('costume_production_detail', {
        p_production_id: productionId!,
      });
      if (error) throw error;
      return data ?? [];
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface AssignCostumesInput {
  costumeId: string;
  scopeKind: CostumeAssignmentScope;
  scopeId: string;
  /** Production whose Command Center views should refresh on success. */
  productionId?: string;
}

/**
 * Bulk-assign a costume to every dancer in a scope (a company, a class, or a
 * single dance). Returns the number of assignments created/affected.
 */
export function useAssignCostumes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AssignCostumesInput): Promise<number> => {
      const { data, error } = await supabase.rpc('assign_costumes', {
        p_costume_id: input.costumeId,
        p_scope_kind: input.scopeKind,
        p_scope_id: input.scopeId,
      });
      if (error) throw error;
      return data ?? 0;
    },
    onSuccess: (_count, input) => {
      void qc.invalidateQueries({ queryKey: ['costumes'] });
      if (input.productionId) {
        void qc.invalidateQueries({
          queryKey: ['costumes', 'production-detail', input.productionId],
        });
        void qc.invalidateQueries({
          queryKey: ['costumes', 'status-report', input.productionId],
        });
      }
    },
  });
}

export interface RecommendSizeInput {
  assignmentId: string;
  /** Production whose Command Center views should refresh on success. */
  productionId?: string;
}

/**
 * Compute and persist a size recommendation for an existing assignment.
 * Returns the stored `size_recommendations` row.
 */
export function useRecommendSize() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RecommendSizeInput): Promise<RpcReturns<'recommend_size'>> => {
      const { data, error } = await supabase.rpc('recommend_size', {
        p_assignment_id: input.assignmentId,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_row, input) => {
      void qc.invalidateQueries({ queryKey: ['costumes'] });
      if (input.productionId) {
        void qc.invalidateQueries({
          queryKey: ['costumes', 'production-detail', input.productionId],
        });
      }
    },
  });
}

/**
 * Preview a recommended size for a dancer + costume pair without persisting.
 * Useful before creating an assignment. Narrows the `Json` payload to
 * {@link SizeRecommendationPreview}.
 */
export function useRecommendSizePreview() {
  return useMutation({
    mutationFn: async (input: {
      dancerId: string;
      costumeId: string;
    }): Promise<SizeRecommendationPreview> => {
      const { data, error } = await supabase.rpc('recommend_size_preview', {
        p_dancer_id: input.dancerId,
        p_costume_id: input.costumeId,
      });
      if (error) throw error;
      return narrowPreview(data);
    },
  });
}

// Insert/Update payload for a dancer's measurement profile, sourced from the
// generated table types so columns stay honest.
export type MeasurementProfileInsert =
  Database['public']['Tables']['measurement_profiles']['Insert'];

/**
 * Create or update a dancer's measurement profile. Upserts on `dancer_id` so a
 * studio keeps one current profile per dancer; `studio_id` is filled by the
 * row's DB default / RLS, so callers need only supply measurements.
 *
 * TODO(measurement-entry): wire this into a measurement-entry form on the
 * Command Center (v1 ships read-only readiness + assignments).
 */
export function useUpsertMeasurementProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: MeasurementProfileInsert) => {
      const { data, error } = await supabase
        .from('measurement_profiles')
        .upsert(stripUndefined(input), { onConflict: 'dancer_id' })
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // A new measurement can change every recommendation, so refresh broadly.
      void qc.invalidateQueries({ queryKey: ['costumes'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Coerce the `Json` recommend_size_preview payload into a typed preview. */
function narrowPreview(value: unknown): SizeRecommendationPreview {
  const obj = (value ?? {}) as Record<string, unknown>;
  const size = typeof obj.size === 'string' ? obj.size : null;
  const confidence = typeof obj.confidence === 'number' ? obj.confidence : null;
  const flags = Array.isArray(obj.flags)
    ? obj.flags.filter((flag): flag is string => typeof flag === 'string')
    : [];
  return { size, confidence, flags };
}

/** Narrow the `Json` `flags` column on a detail row to a string[] of codes. */
export function costumeFlags(flags: CostumeProductionDetailRow['flags']): string[] {
  return Array.isArray(flags)
    ? flags.filter((flag): flag is string => typeof flag === 'string')
    : [];
}
